import * as React from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import {
  type Control,
  Controller,
  type FieldValues,
  type Path,
} from "react-hook-form";

type AutocompleteOption = {
  label: string;
  value: string;
};

type Props<T extends FieldValues> = {
  name: Path<T>;
  control: Control<T>;
  options: AutocompleteOption[];
  label: string;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  onChange?: (value: string[]) => void;
};

export const MultiAutocompleteWithControl = <T extends FieldValues>({
  name,
  control,
  options,
  label,
  placeholder,
  error,
  helperText,
  disabled,
  onChange,
}: Props<T>) => {
  return (
    <Controller<T>
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const { ref, ...restField } = field;
        const currentValue = Array.isArray(restField.value)
          ? options.filter((opt) => restField.value.includes(opt.value))
          : [];

        return (
          <Autocomplete<AutocompleteOption, true>
            {...restField}
            multiple
            value={currentValue}
            onChange={(_, newValue) => {
              const values = newValue.map((item) => item.value);
              restField.onChange(values);
              if (onChange) onChange(values);
            }}
            options={options}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            disabled={disabled}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                placeholder={placeholder}
                error={error || !!fieldState.error?.message}
                helperText={helperText || fieldState.error?.message}
                size="small"
                inputRef={(node) => {
                  const inputPropsRef = params.InputProps.ref;
                  if (typeof inputPropsRef === "function") {
                    inputPropsRef(node);
                  } else if (inputPropsRef) {
                    (
                      inputPropsRef as React.MutableRefObject<HTMLInputElement | null>
                    ).current = node;
                  }

                  if (typeof ref === "function") {
                    ref(node);
                  } else if (ref) {
                    (
                      ref as React.MutableRefObject<HTMLInputElement | null>
                    ).current = node;
                  }
                }}
                inputProps={{
                  ...params.inputProps,
                  name: field.name,
                }}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option.label}
                  size="small"
                  {...getTagProps({ index })}
                  key={option.value}
                />
              ))
            }
          />
        );
      }}
    />
  );
};
